import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

function localDate(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function todayStr() { return localDate(); }
function dueIn7(from) { const d = new Date(from); d.setDate(d.getDate() + 7); return localDate(d); }

const PAYMENT_METHODS = [
  { value: 'transfer', label: 'Przelew' },
  { value: 'cash',     label: 'Gotówka' },
  { value: 'card',     label: 'Karta'   },
  { value: 'blik',     label: 'BLIK'    },
];

const PAYMENT_LABELS = { transfer: 'Przelew', cash: 'Gotówka', card: 'Karta', blik: 'BLIK' };

const KSEF_STATUS = {
  pending:  { label: 'Oczekuje',      color: 'var(--amber)',     bg: 'var(--amber-light)' },
  sent:     { label: 'Wysłana',       color: 'var(--blue-dark)', bg: 'var(--blue-light)'  },
  accepted: { label: 'Zaakceptowana', color: 'var(--green)',     bg: 'var(--green-light)' },
  rejected: { label: 'Odrzucona',     color: 'var(--red)',       bg: '#FCEBEB'            },
};

const TYPE_LABELS = {
  receipt_nip:    { label: 'Paragon z NIP', icon: '🧾' },
  invoice:        { label: 'Faktura VAT',   icon: '📄' },
  invoice_custom: { label: 'Faktura ręczna', icon: '✍️' },
};

function today()   { return localDate(); }
function weekAgo() { const d = new Date(); d.setDate(d.getDate() - 6); return localDate(d); }
function firstDayPrevMonth() { const d = new Date(); return localDate(new Date(d.getFullYear(), d.getMonth() - 1, 1)); }
function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function getToken() { return window.__cafeApiToken || localStorage.getItem('cafepos_token'); }
async function apiFetch(method, path, body) {
  const res = await fetch(`/api${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Błąd serwera');
  return data;
}

// ---------------------------------------------------------------------------
// Modal — faktura ręczna managera/admina
// ---------------------------------------------------------------------------
function CustomInvoiceModal({ onClose, onCreated }) {
  const [buyer, setBuyer] = useState({ buyer_name: '', buyer_nip: '', buyer_address: '', buyer_city: '', buyer_zip: '', buyer_is_jst: false, buyer_is_vat_group: false });
  const [items, setItems] = useState([{ name: '', qty: 1, net_price: '', vat_rate: 23 }]);
  const [priceMode, setPriceMode] = useState('netto'); // 'netto' | 'brutto'
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [notes, setNotes]   = useState('');
  const [gusLoading, setGusLoading] = useState(false);
  const [hasRecipient, setHasRecipient] = useState(false);
  const [recipient, setRecipient] = useState({ name: '', nip: '', address: '', city: '', zip: '' });
  const [recGusLoading, setRecGusLoading] = useState(false);
  const [dates, setDates] = useState({
    issue_date:     todayStr(),
    sale_date:      todayStr(),
    payment_due:    dueIn7(todayStr()),
    payment_method: 'transfer',
  });

  async function lookupGus() {
    const nip = buyer.buyer_nip.replace(/[\s-]/g, '');
    if (nip.length !== 10) { setError('Wpisz poprawny NIP (10 cyfr) przed wyszukaniem'); return; }
    setGusLoading(true); setError('');
    try {
      const res  = await apiFetch('GET', `/invoices/gus/${nip}`);
      setBuyer(b => ({ ...b, buyer_name: res.name, buyer_address: res.address, buyer_zip: res.zip, buyer_city: res.city }));
    } catch (err) { setError(`GUS: ${err.message}`); }
    finally { setGusLoading(false); }
  }

  function setB(k, v) { setBuyer(b => ({ ...b, [k]: v })); }
  function setRec(k, v) { setRecipient(r => ({ ...r, [k]: v })); }

  async function lookupGusRecipient() {
    const nip = recipient.nip.replace(/[\s-]/g, '');
    if (nip.length !== 10) return;
    setRecGusLoading(true);
    try {
      const res = await apiFetch('GET', `/invoices/gus/${nip}`);
      setRecipient(r => ({ ...r, name: res.name, address: res.address, zip: res.zip, city: res.city }));
    } catch (err) { setError(`GUS (odbiorca): ${err.message}`); }
    finally { setRecGusLoading(false); }
  }
  function setItem(i, k, v) { setItems(arr => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it)); }
  function addItem() { setItems(arr => [...arr, { name: '', qty: 1, net_price: '', vat_rate: 23 }]); }
  function removeItem(i) { setItems(arr => arr.filter((_, idx) => idx !== i)); }

  const gross = items.reduce((s, it) => {
    const val = Number(it.net_price) || 0;
    const qty = Number(it.qty);
    const vr  = Number(it.vat_rate) / 100;
    return s + (priceMode === 'brutto' ? val * qty : val * qty * (1 + vr));
  }, 0);

  async function submit() {
    setSaving(true); setError('');
    try {
      const result = await apiFetch('POST', '/invoices', {
        type: 'invoice_custom',
        ...buyer,
        ...dates,
        notes: notes || undefined,
        custom_items: items.map(it => {
          const vr = Number(it.vat_rate) / 100;
          const net = priceMode === 'brutto'
            ? Number(it.net_price) / (1 + vr)
            : Number(it.net_price);
          return { name: it.name, qty: Number(it.qty), net_price: net, vat_rate: Number(it.vat_rate) };
        }),
        ...(hasRecipient && recipient.name ? {
          recipient_name: recipient.name, recipient_nip: recipient.nip,
          recipient_address: recipient.address, recipient_city: recipient.city, recipient_zip: recipient.zip,
        } : {}),
      });
      onCreated(result);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const canSubmit = buyer.buyer_name && buyer.buyer_nip && buyer.buyer_address && buyer.buyer_city && buyer.buyer_zip
    && items.every(it => it.name && it.net_price > 0 && it.qty > 0);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>✍️ Faktura ręczna</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)' }}>×</button>
        </div>

        {error && <div style={{ background: '#FCEBEB', color: 'var(--red)', fontSize: 13, padding: '10px 12px', borderRadius: 6, marginBottom: 14 }}>{error}</div>}

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase' }}>Dane nabywcy</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
          <div className="field" style={{ gridColumn: '1/-1' }}>
            <label>Nazwa nabywcy *</label>
            <input value={buyer.buyer_name} onChange={e => setB('buyer_name', e.target.value)} placeholder="Firma Sp. z o.o." />
          </div>
          <div className="field" style={{ gridColumn: '1/-1' }}>
            <label>NIP *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={buyer.buyer_nip} onChange={e => setB('buyer_nip', e.target.value)} placeholder="0000000000" maxLength={13}
                style={{ flex: 1 }} onKeyDown={e => e.key === 'Enter' && lookupGus()} />
              <button type="button" onClick={lookupGus} disabled={gusLoading}
                style={{ padding: '0 14px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--bg)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                {gusLoading ? '⏳' : '🔍 GUS'}
              </button>
            </div>
          </div>
          <div className="field" style={{ gridColumn: '1/-1' }}>
            <label>Adres *</label>
            <input value={buyer.buyer_address} onChange={e => setB('buyer_address', e.target.value)} placeholder="ul. Przykładowa 1" />
          </div>
          <div className="field">
            <label>Kod pocztowy *</label>
            <input value={buyer.buyer_zip} onChange={e => setB('buyer_zip', e.target.value)} placeholder="00-000" />
          </div>
          <div className="field">
            <label>Miasto *</label>
            <input value={buyer.buyer_city} onChange={e => setB('buyer_city', e.target.value)} placeholder="Warszawa" />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '2px 0 8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={buyer.buyer_is_jst} onChange={e => setB('buyer_is_jst', e.target.checked)} />
            Nabywca jest jednostką samorządu terytorialnego (JST)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={buyer.buyer_is_vat_group} onChange={e => setB('buyer_is_vat_group', e.target.checked)} />
            Nabywca należy do grupy VAT
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Pozycje faktury</div>
          <div style={{ display: 'flex', border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden', fontSize: 13 }}>
            {['netto', 'brutto'].map(mode => (
              <button key={mode} onClick={() => setPriceMode(mode)}
                style={{ padding: '5px 14px', border: 'none', cursor: 'pointer', fontWeight: 500,
                  background: priceMode === mode ? 'var(--blue)' : 'var(--surface)',
                  color: priceMode === mode ? '#fff' : 'var(--text-muted)',
                }}>
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 60px 100px 80px 32px', gap: 6, marginBottom: 8, alignItems: 'center' }}>
            <input value={it.name} onChange={e => setItem(i, 'name', e.target.value)}
              placeholder="Nazwa usługi / towaru"
              style={{ padding: '9px 10px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13 }} />
            <input type="number" value={it.qty} min={1} onChange={e => setItem(i, 'qty', e.target.value)}
              style={{ padding: '9px 8px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13, textAlign: 'center' }} />
            <input type="number" value={it.net_price} onChange={e => setItem(i, 'net_price', e.target.value)}
              placeholder={priceMode === 'brutto' ? 'Cena brutto' : 'Cena netto'}
              style={{ padding: '9px 8px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13 }} />
            <select value={it.vat_rate} onChange={e => setItem(i, 'vat_rate', e.target.value)}
              style={{ padding: '9px 6px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--surface)' }}>
              <option value="23">23%</option>
              <option value="8">8%</option>
              <option value="5">5%</option>
              <option value="0">ZW</option>
            </select>
            {items.length > 1
              ? <button onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: 'var(--red)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
              : <div />}
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <button className="btn-sm" onClick={addItem}>+ Dodaj pozycję</button>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Brutto: {gross.toFixed(2)} zł</div>
        </div>

        {/* Uwagi */}
        <div style={{ borderTop: '0.5px solid var(--border)', margin: '8px 0', paddingTop: 12 }}>
          <div className="field">
            <label>Uwagi <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(opcjonalne, pojawi się w DodatkowyOpis w XML)</span></label>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="np. PSW, nr zamówienia..." />
          </div>
        </div>

        {/* Odbiorca */}
        <div style={{ borderTop: '0.5px solid var(--border)', margin: '8px 0', paddingTop: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            <input type="checkbox" checked={hasRecipient} onChange={e => setHasRecipient(e.target.checked)} />
            Dodaj odbiorcę (inny niż nabywca)
          </label>
        </div>

        {hasRecipient && (
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 10, textTransform: 'uppercase' }}>Dane odbiorcy</div>
            <div className="field" style={{ margin: '0 0 8px' }}>
              <label>NIP odbiorcy *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={recipient.nip} onChange={e => setRec('nip', e.target.value)} placeholder="0000000000" maxLength={13}
                  style={{ flex: 1 }} onKeyDown={e => e.key === 'Enter' && lookupGusRecipient()} />
                <button type="button" onClick={lookupGusRecipient} disabled={recGusLoading}
                  style={{ padding: '0 14px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--surface)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                  {recGusLoading ? '⏳' : '🔍 GUS'}
                </button>
              </div>
            </div>
            <div className="field" style={{ margin: '0 0 8px' }}><label>Nazwa odbiorcy *</label><input value={recipient.name} onChange={e => setRec('name', e.target.value)} placeholder="Firma Sp. z o.o." /></div>
            <div className="field" style={{ margin: '0 0 8px' }}><label>Adres</label><input value={recipient.address} onChange={e => setRec('address', e.target.value)} placeholder="ul. Przykładowa 1" /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
              <div className="field" style={{ margin: 0 }}><label>Kod pocztowy</label><input value={recipient.zip} onChange={e => setRec('zip', e.target.value)} placeholder="00-000" /></div>
              <div className="field" style={{ margin: 0 }}><label>Miasto</label><input value={recipient.city} onChange={e => setRec('city', e.target.value)} placeholder="Warszawa" /></div>
            </div>
          </div>
        )}

        {/* Daty i płatność */}
        <div style={{ borderTop: '0.5px solid var(--border)', margin: '8px 0', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase' }}>Daty i płatność</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <div className="field" style={{ margin: '0 0 8px' }}>
              <label>Data wystawienia</label>
              <input type="date" value={dates.issue_date}
                onChange={e => setDates(d => ({ ...d, issue_date: e.target.value, payment_due: dueIn7(e.target.value) }))} />
            </div>
            <div className="field" style={{ margin: '0 0 8px' }}>
              <label>Data sprzedaży</label>
              <input type="date" value={dates.sale_date}
                onChange={e => setDates(d => ({ ...d, sale_date: e.target.value }))} />
            </div>
            <div className="field" style={{ margin: '0 0 8px' }}>
              <label>Termin zapłaty</label>
              <input type="date" value={dates.payment_due}
                onChange={e => setDates(d => ({ ...d, payment_due: e.target.value }))} />
            </div>
            <div className="field" style={{ margin: '0 0 8px' }}>
              <label>Forma płatności</label>
              <select value={dates.payment_method} onChange={e => setDates(d => ({ ...d, payment_method: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--surface)', fontSize: 14 }}>
                {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-outline" onClick={onClose} style={{ flex: 1 }}>Anuluj</button>
          <button className="btn-primary" onClick={submit} disabled={saving || !canSubmit} style={{ flex: 2 }}>
            {saving ? 'Wystawianie...' : 'Wystaw fakturę'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal — szczegóły faktury
// ---------------------------------------------------------------------------
function InvoiceDetailModal({ invoiceId, onClose, onSentKSeF, onSaved }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [msg, setMsg]         = useState('');
  const [editModal, setEditModal] = useState(false);

  function loadInvoice() {
    setLoading(true);
    apiFetch('GET', `/invoices/${invoiceId}`)
      .then(setInvoice).catch(e => setMsg(e.message)).finally(() => setLoading(false));
  }

  useEffect(() => { loadInvoice(); }, [invoiceId]);

  async function sendKSeF() {
    setSending(true); setMsg('');
    try {
      const res = await apiFetch('POST', `/invoices/${invoiceId}/send-ksef`);
      setMsg(`✓ Wysłano do KSeF. Nr ref: ${res.ksef_reference_no}`);
      setInvoice(i => ({ ...i, ksef_status: 'sent', ksef_reference_no: res.ksef_reference_no }));
      onSentKSeF?.();
    } catch (err) { setMsg(`✗ ${err.message}`); }
    finally { setSending(false); }
  }

  async function downloadXml() {
    const res = await fetch(`/api/invoices/${invoiceId}/xml`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const xml  = await res.text();
    const blob = new Blob([xml], { type: 'application/xml' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${invoice?.invoice_number?.replace(/\//g, '-') || 'faktura'}.xml`; a.click();
    URL.revokeObjectURL(url);
  }

  async function printConfirmation() {
    setSending(true); setMsg('');
    try {
      await apiFetch('POST', `/invoices/${invoiceId}/print-confirmation`);
      setMsg('✓ Potwierdzenie transakcji wydrukowane');
    } catch (err) { setMsg(`✗ ${err.message}`); }
    finally { setSending(false); }
  }

  const st = invoice ? (KSEF_STATUS[invoice.ksef_status] || KSEF_STATUS.pending) : null;
  const tp = invoice ? (TYPE_LABELS[invoice.type] || TYPE_LABELS.invoice) : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{tp?.icon} {invoice?.invoice_number || '...'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)' }}>×</button>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Ładowanie...</div>}

        {invoice && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: 'var(--bg)', color: 'var(--text-muted)' }}>
                {tp?.label}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20, color: st.color, background: st.bg }}>
                {st.label}
              </span>
              {invoice.ksef_reference_no && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', padding: '3px 0' }}>Nr ref: {invoice.ksef_reference_no}</span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: invoice.recipient_name ? '1fr 1fr 1fr' : '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>NABYWCA</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{invoice.buyer_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>NIP: {invoice.buyer_nip}</div>
                {invoice.buyer_address && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{invoice.buyer_address}</div>}
                {invoice.buyer_city && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{invoice.buyer_zip} {invoice.buyer_city}</div>}
              </div>
              {invoice.recipient_name && (
                <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>ODBIORCA</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{invoice.recipient_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>NIP: {invoice.recipient_nip}</div>
                  {invoice.recipient_address && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{invoice.recipient_address}</div>}
                  {invoice.recipient_city && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{invoice.recipient_zip} {invoice.recipient_city}</div>}
                </div>
              )}
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>KWOTY</div>
                <div style={{ fontSize: 13 }}>Netto: <strong>{Number(invoice.net_total).toFixed(2)} zł</strong></div>
                <div style={{ fontSize: 13 }}>VAT {invoice.vat_rate}%: <strong>{Number(invoice.vat_total).toFixed(2)} zł</strong></div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>Brutto: {Number(invoice.gross_total).toFixed(2)} zł</div>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                POZYCJE {invoice.order_id ? `ZAMÓWIENIA #${invoice.order_id}` : ''}
              </div>
              {(invoice.items || []).map((item, i) => {
                const price = item.price ?? (Number(item.net_price) * (1 + Number(item.vat_rate ?? invoice.vat_rate) / 100));
                return (
                  <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '0.5px solid var(--border)', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-muted)', minWidth: 24 }}>{item.qty}×</span>
                    <span style={{ flex: 1 }}>{item.name}{item.variant_label ? ` — ${item.variant_label}` : ''}</span>
                    <span style={{ fontWeight: 500 }}>{(price * item.qty).toFixed(2)} zł</span>
                  </div>
                );
              })}
            </div>

            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
              {invoice.issue_date && <span>Wystawiona: <strong>{String(invoice.issue_date).split('T')[0]}</strong></span>}
              {invoice.sale_date  && <span>Sprzedaż: <strong>{String(invoice.sale_date).split('T')[0]}</strong></span>}
              {invoice.payment_due && <span>Termin zapłaty: <strong>{String(invoice.payment_due).split('T')[0]}</strong></span>}
              {invoice.payment_method && <span>Płatność: <strong>{PAYMENT_LABELS[invoice.payment_method] || invoice.payment_method}</strong></span>}
              {invoice.issuer_name && <span>Wystawiający: <strong>{invoice.issuer_name}</strong></span>}
              {invoice.ksef_sent_at && <span>Wysłano KSeF: <strong>{formatDate(invoice.ksef_sent_at)}</strong></span>}
            </div>

            {msg && (
              <div style={{ fontSize: 13, padding: '8px 12px', borderRadius: 6, marginBottom: 12,
                background: msg.startsWith('✓') ? 'var(--green-light)' : '#FCEBEB',
                color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>
                {msg}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-outline" onClick={downloadXml} style={{ flex: 1, fontSize: 13 }}>⬇ XML</button>
              {invoice.ksef_status !== 'accepted' && invoice.ksef_status !== 'sent' && (
                <button className="btn-outline" onClick={() => setEditModal(true)} style={{ flex: 1, fontSize: 13 }}>✏️ Edytuj</button>
              )}
              {invoice.ksef_status !== 'accepted' && (
                <button className="btn-primary" onClick={sendKSeF} disabled={sending} style={{ flex: 2, fontSize: 13 }}>
                  {sending ? 'Wysyłanie...' : invoice.ksef_status === 'sent' ? '🔄 Wyślij ponownie' : '📤 Wyślij do KSeF'}
                </button>
              )}
            </div>
            {invoice.ksef_status !== 'accepted' && (
              <button className="btn-outline" onClick={printConfirmation} disabled={sending} style={{ width: '100%', fontSize: 13, marginTop: 8 }}>
                🖨 Potwierdzenie transakcji (drukarka fiskalna)
              </button>
            )}
            {editModal && (
              <EditInvoiceModal
                invoice={invoice}
                onClose={() => setEditModal(false)}
                onSaved={() => { setEditModal(false); loadInvoice(); onSaved?.(); }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Główna strona faktur
// ---------------------------------------------------------------------------
export default function Invoices() {
  const { effectiveRole } = useAuth();
  const isManagerOrAdmin = ['admin', 'manager'].includes(effectiveRole);

  const [from, setFrom]           = useState(firstDayPrevMonth());
  const [to, setTo]               = useState(today());
  const [statusFilter, setStatus] = useState('');
  const [typeFilter, setType]     = useState('');
  const [invoices, setInvoices]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [detailId, setDetailId]   = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [confirmingId, setConfirmingId] = useState(null);

  const loadPendingTransfers = useCallback(async () => {
    try { setPendingTransfers(await apiFetch('GET', '/orders/pending-transfers')); }
    catch (e) { console.error(e); }
  }, []);

  async function confirmTransfer(orderId) {
    setConfirmingId(orderId);
    try {
      await apiFetch('PATCH', `/orders/${orderId}/confirm-transfer`);
      await loadPendingTransfers();
    } catch (e) { alert(`Błąd: ${e.message}`); }
    finally { setConfirmingId(null); }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from, to });
      if (statusFilter) params.set('ksef_status', statusFilter);
      if (typeFilter)   params.set('type', typeFilter);
      setInvoices(await apiFetch('GET', `/invoices?${params}`));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [from, to, statusFilter, typeFilter]);

  useEffect(() => { load(); loadPendingTransfers(); }, []); // eslint-disable-line

  const counts = invoices ? {
    total: invoices.length,
    sum:   invoices.reduce((s, i) => s + Number(i.gross_total), 0),
    pending:  invoices.filter(i => i.ksef_status === 'pending').length,
    sent:     invoices.filter(i => i.ksef_status === 'sent').length,
    accepted: invoices.filter(i => i.ksef_status === 'accepted').length,
    rejected: invoices.filter(i => i.ksef_status === 'rejected').length,
  } : null;

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 500 }}>🧾 Faktury i paragony z NIP</h2>
        {isManagerOrAdmin && (
          <button className="btn-primary" onClick={() => setShowCustom(true)}
            style={{ width: 'auto', padding: '9px 18px', fontSize: 13 }}>
            ✍️ Faktura ręczna
          </button>
        )}
      </div>

      {pendingTransfers.length > 0 && (
        <div style={{ background: '#FAEEDA', border: '0.5px solid #F0D8A8', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#854F0B', marginBottom: 10 }}>
            ⏳ Oczekujące przelewy ({pendingTransfers.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendingTransfers.map(o => {
              const tableName = o.table_name || `Stolik ${o.table_number}`;
              const dateLabel = new Date(o.created_at).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
              return (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)', borderRadius: 8, padding: '10px 14px' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{tableName}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{dateLabel} · {Number(o.total).toFixed(2)} zł</div>
                  </div>
                  <button className="btn-sm success" disabled={confirmingId === o.id} onClick={() => confirmTransfer(o.id)}>
                    {confirmingId === o.id ? 'Zapisuję...' : '✓ Oznacz jako zapłacone'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filtry */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 16, background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px 16px', flexWrap: 'wrap' }}>
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 130 }}>
          <label>Od</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 130 }}>
          <label>Do</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 130 }}>
          <label>Typ</label>
          <select value={typeFilter} onChange={e => setType(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--surface)', fontSize: 14 }}>
            <option value="">Wszystkie</option>
            <option value="receipt_nip">Paragon z NIP</option>
            <option value="invoice">Faktura VAT</option>
            <option value="invoice_custom">Faktura ręczna</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 130 }}>
          <label>Status KSeF</label>
          <select value={statusFilter} onChange={e => setStatus(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--surface)', fontSize: 14 }}>
            <option value="">Wszystkie</option>
            <option value="pending">Oczekuje</option>
            <option value="sent">Wysłana</option>
            <option value="accepted">Zaakceptowana</option>
            <option value="rejected">Odrzucona</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn-sm" onClick={() => { setFrom(localDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1))); setTo(today()); }}>Ten mies.</button>
          <button className="btn-sm" onClick={() => { setFrom(firstDayPrevMonth()); setTo(today()); }}>Poprzedni</button>
        </div>
        <button className="btn-primary" style={{ width: 'auto', padding: '9px 20px' }} onClick={load} disabled={loading}>
          {loading ? 'Ładowanie...' : 'Szukaj'}
        </button>
      </div>

      {/* Podsumowanie */}
      {counts && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 16 }}>
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Łącznie</div>
            <div style={{ fontSize: 22, fontWeight: 600 }}>{counts.total}</div>
          </div>
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Kwota brutto</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{counts.sum.toFixed(2)} zł</div>
          </div>
          {counts.pending  > 0 && <div style={{ background: 'var(--amber-light)', borderRadius: 8, padding: '12px 14px' }}><div style={{ fontSize: 12, color: 'var(--amber)' }}>Oczekuje</div><div style={{ fontSize: 22, fontWeight: 600, color: 'var(--amber)' }}>{counts.pending}</div></div>}
          {counts.accepted > 0 && <div style={{ background: 'var(--green-light)',  borderRadius: 8, padding: '12px 14px' }}><div style={{ fontSize: 12, color: 'var(--green)' }}>Zaakceptowane</div><div style={{ fontSize: 22, fontWeight: 600, color: 'var(--green)' }}>{counts.accepted}</div></div>}
          {counts.rejected > 0 && <div style={{ background: '#FCEBEB',             borderRadius: 8, padding: '12px 14px' }}><div style={{ fontSize: 12, color: 'var(--red)' }}>Odrzucone</div><div style={{ fontSize: 22, fontWeight: 600, color: 'var(--red)' }}>{counts.rejected}</div></div>}
        </div>
      )}

      {/* Lista */}
      {invoices && invoices.length === 0 && (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)', fontSize: 14 }}>
          Brak dokumentów w wybranym okresie
        </div>
      )}

      {invoices && invoices.map(inv => {
        const st = KSEF_STATUS[inv.ksef_status] || KSEF_STATUS.pending;
        const tp = TYPE_LABELS[inv.type] || TYPE_LABELS.invoice;
        const tableName = inv.table_name ? (inv.table_name || `Stolik ${inv.table_number}`) : null;
        return (
          <div key={inv.id} onClick={() => setDetailId(inv.id)}
            style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, marginBottom: 8, padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#aaa'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{inv.invoice_number}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{inv.buyer_name} · NIP {inv.buyer_nip}</div>
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{tp.icon} {tp.label}</span>
            {inv.payment_method && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 60 }}>
                {PAYMENT_LABELS[inv.payment_method] || inv.payment_method}
              </span>
            )}
            {inv.payment_due && String(inv.payment_due) !== 'null' && (
              <span style={{ fontSize: 12, color: inv.payment_due && new Date(inv.payment_due) < new Date() && inv.ksef_status !== 'accepted' ? 'var(--red)' : 'var(--text-muted)', minWidth: 80 }}>
                do {String(inv.payment_due).split('T')[0]}
              </span>
            )}
            {tableName && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right', minWidth: 70 }}>
                <div>{tableName}</div>
                {inv.order_id && <div>#{inv.order_id}</div>}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 105, textAlign: 'right' }}>{formatDate(inv.created_at)}</div>
            <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap', color: st.color, background: st.bg }}>{st.label}</span>
            <span style={{ fontSize: 15, fontWeight: 700, minWidth: 80, textAlign: 'right' }}>{Number(inv.gross_total).toFixed(2)} zł</span>
          </div>
        );
      })}

      {showCustom && (
        <CustomInvoiceModal
          onClose={() => setShowCustom(false)}
          onCreated={result => { setShowCustom(false); setDetailId(result.id); load(); }}
        />
      )}
      {detailId && (
        <InvoiceDetailModal invoiceId={detailId} onClose={() => setDetailId(null)} onSentKSeF={load} onSaved={load} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal edycji faktury
// ---------------------------------------------------------------------------
function EditInvoiceModal({ invoice, onClose, onSaved }) {
  const isCustom = invoice.type === 'invoice_custom';
  const [buyer, setBuyer] = useState({
    buyer_name:    invoice.buyer_name    || '',
    buyer_nip:     invoice.buyer_nip     || '',
    buyer_address: invoice.buyer_address || '',
    buyer_city:    invoice.buyer_city    || '',
    buyer_zip:     invoice.buyer_zip     || '',
    buyer_is_jst:       !!invoice.buyer_is_jst,
    buyer_is_vat_group: !!invoice.buyer_is_vat_group,
  });
  const [dates, setDates] = useState({
    issue_date:     String(invoice.issue_date || '').split('T')[0],
    sale_date:      String(invoice.sale_date  || '').split('T')[0],
    payment_due:    String(invoice.payment_due|| '').split('T')[0],
    payment_method: invoice.payment_method || 'transfer',
  });
  const [notes, setNotes] = useState(invoice.notes || '');
  const [items, setItems] = useState(
    (invoice.items || []).map(it => ({
      name:      it.name || '',
      qty:       it.qty  || 1,
      net_price: (it.net_price ?? Number(it.price) / (1 + Number(it.vat_rate ?? 23) / 100)).toFixed(2),
      vat_rate:  it.vat_rate ?? 23,
    }))
  );
  const [priceMode, setPriceMode] = useState('netto');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [gusLoading, setGusLoading] = useState(false);

  function setB(k, v) { setBuyer(b => ({ ...b, [k]: v })); }
  function setItem(i, k, v) { setItems(arr => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it)); }
  function addItem() { setItems(arr => [...arr, { name: '', qty: 1, net_price: '', vat_rate: 23 }]); }
  function removeItem(i) { setItems(arr => arr.filter((_, idx) => idx !== i)); }

  async function lookupGus() {
    const nip = buyer.buyer_nip.replace(/[\s-]/g, '');
    if (nip.length !== 10) return;
    setGusLoading(true);
    try {
      const res = await apiFetch('GET', `/invoices/gus/${nip}`);
      setBuyer(b => ({ ...b, buyer_name: res.name, buyer_address: res.address, buyer_zip: res.zip, buyer_city: res.city }));
    } catch (err) { setError(`GUS: ${err.message}`); }
    finally { setGusLoading(false); }
  }

  async function submit() {
    setSaving(true); setError('');
    try {
      const body = { ...buyer, ...dates, notes };
      if (isCustom) {
        body.custom_items = items.map(it => {
          const vr = Number(it.vat_rate) / 100;
          const net = priceMode === 'brutto' ? Number(it.net_price) / (1 + vr) : Number(it.net_price);
          return { name: it.name, qty: Number(it.qty), net_price: net, vat_rate: Number(it.vat_rate) };
        });
      }
      await apiFetch('PATCH', `/invoices/${invoice.id}`, body);
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const gross = isCustom ? items.reduce((s, it) => {
    const val = Number(it.net_price) || 0, qty = Number(it.qty), vr = Number(it.vat_rate) / 100;
    return s + (priceMode === 'brutto' ? val * qty : val * qty * (1 + vr));
  }, 0) : Number(invoice.gross_total);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>✏️ Edytuj fakturę {invoice.invoice_number}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        {error && <div style={{ background: '#FCEBEB', color: 'var(--red)', fontSize: 13, padding: '10px 12px', borderRadius: 6, marginBottom: 14 }}>{error}</div>}

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase' }}>Dane nabywcy</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
          <div className="field" style={{ gridColumn: '1/-1' }}>
            <label>Nazwa nabywcy *</label>
            <input value={buyer.buyer_name} onChange={e => setB('buyer_name', e.target.value)} />
          </div>
          <div className="field" style={{ gridColumn: '1/-1' }}>
            <label>NIP *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={buyer.buyer_nip} onChange={e => setB('buyer_nip', e.target.value)} maxLength={13} style={{ flex: 1 }} />
              <button type="button" onClick={lookupGus} disabled={gusLoading}
                style={{ padding: '0 14px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--bg)', fontSize: 13, cursor: 'pointer', color: 'var(--text-muted)' }}>
                {gusLoading ? '⏳' : '🔍 GUS'}
              </button>
            </div>
          </div>
          <div className="field" style={{ gridColumn: '1/-1' }}><label>Adres *</label><input value={buyer.buyer_address} onChange={e => setB('buyer_address', e.target.value)} /></div>
          <div className="field"><label>Kod pocztowy *</label><input value={buyer.buyer_zip} onChange={e => setB('buyer_zip', e.target.value)} /></div>
          <div className="field"><label>Miasto *</label><input value={buyer.buyer_city} onChange={e => setB('buyer_city', e.target.value)} /></div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 0 8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={buyer.buyer_is_jst} onChange={e => setB('buyer_is_jst', e.target.checked)} />
            Nabywca jest jednostką samorządu terytorialnego (JST)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={buyer.buyer_is_vat_group} onChange={e => setB('buyer_is_vat_group', e.target.checked)} />
            Nabywca należy do grupy VAT
          </label>
        </div>

        {isCustom && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Pozycje faktury</div>
              <div style={{ display: 'flex', border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden', fontSize: 13 }}>
                {['netto','brutto'].map(mode => (
                  <button key={mode} onClick={() => setPriceMode(mode)}
                    style={{ padding: '5px 14px', border: 'none', cursor: 'pointer', fontWeight: 500,
                      background: priceMode === mode ? 'var(--blue)' : 'var(--surface)',
                      color: priceMode === mode ? '#fff' : 'var(--text-muted)' }}>
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {items.map((it, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 60px 100px 80px 32px', gap: 6, marginBottom: 8, alignItems: 'center' }}>
                <input value={it.name} onChange={e => setItem(i, 'name', e.target.value)} placeholder="Nazwa" style={{ padding: '9px 10px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13 }} />
                <input type="number" value={it.qty} min={1} onChange={e => setItem(i, 'qty', e.target.value)} style={{ padding: '9px 8px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13, textAlign: 'center' }} />
                <input type="number" value={it.net_price} onChange={e => setItem(i, 'net_price', e.target.value)} placeholder={priceMode === 'brutto' ? 'Cena brutto' : 'Cena netto'} style={{ padding: '9px 8px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13 }} />
                <select value={it.vat_rate} onChange={e => setItem(i, 'vat_rate', e.target.value)} style={{ padding: '9px 6px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 13, background: 'var(--surface)' }}>
                  <option value="23">23%</option><option value="8">8%</option><option value="5">5%</option><option value="0">ZW</option>
                </select>
                {items.length > 1 ? <button onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: 'var(--red)', fontSize: 18, cursor: 'pointer' }}>×</button> : <div />}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <button className="btn-sm" onClick={addItem}>+ Dodaj pozycję</button>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Brutto: {gross.toFixed(2)} zł</div>
            </div>
          </>
        )}

        <div className="field"><label>Uwagi</label><input value={notes} onChange={e => setNotes(e.target.value)} placeholder="np. PSW, nr zamówienia..." /></div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px', marginTop: 8 }}>
          <div className="field"><label>Data wystawienia</label><input type="date" value={dates.issue_date} onChange={e => setDates(d => ({ ...d, issue_date: e.target.value }))} /></div>
          <div className="field"><label>Data sprzedaży</label><input type="date" value={dates.sale_date} onChange={e => setDates(d => ({ ...d, sale_date: e.target.value }))} /></div>
          <div className="field"><label>Termin zapłaty</label><input type="date" value={dates.payment_due} onChange={e => setDates(d => ({ ...d, payment_due: e.target.value }))} /></div>
          <div className="field">
            <label>Forma płatności</label>
            <select value={dates.payment_method} onChange={e => setDates(d => ({ ...d, payment_method: e.target.value }))} style={{ width: '100%', padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--surface)', fontSize: 14 }}>
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button className="btn-outline" onClick={onClose} style={{ flex: 1 }}>Anuluj</button>
          <button className="btn-primary" onClick={submit} disabled={saving} style={{ flex: 2 }}>
            {saving ? 'Zapisuję...' : 'Zapisz zmiany'}
          </button>
        </div>
      </div>
    </div>
  );
}
